import Constants from '@/Constants';
import { AvailableLanguage } from '@/models/AvailableLanguage';
import { ViewType } from '@/models/ViewType';
import { AnyAction, Reducer } from 'redux';
import { PURGE } from 'redux-persist';
import _ from 'underscore';
import { ContentEnrollment } from '../content/types';
import { ContentStudentView, ItemTypes, Itens, ItensState, ListContentStudentViewData } from './types';

const initialStateContentList: ListContentStudentViewData = {
    list: [] as ContentStudentView[],
    total: 0,
    loading: true,
};

const INITIAL_STATE: ItensState = {
    data: {} as Itens,
    listData: {
        listLastPublishedStudentView: { ...initialStateContentList },
        listMostAccessedStudentView: { ...initialStateContentList },
        listInProgressStudentView: { ...initialStateContentList },
        listCompletedStudentView: { ...initialStateContentList },
        listTopRatedStudentView: { ...initialStateContentList },
        listFavoriteStudentView: { ...initialStateContentList },
        listLikeYouStudentView: { ...initialStateContentList },
        listForYouStudentView: { ...initialStateContentList },
        listSearchResultStudentView: { ...initialStateContentList },
        listMyCourseStudentView: { ...initialStateContentList },
        listSuggestedStudentView: { ...initialStateContentList },
        listEssentialStudentView: { ...initialStateContentList },
        listTrainingsEnrolledStudentView: { ...initialStateContentList },
    },
    currentData: {} as ContentStudentView,
    loading: true,
    hasError: false,
    refreshItem: undefined,
    error: undefined,
    success: false,
};

const processContentRequestSuccess = (state: ItensState, action: AnyAction, listName: ViewType) => {
    const currentList: ContentStudentView[] = [...state.listData[listName].list];
    const newList: ContentStudentView[] = [...action.payload.data];
    const mergedlist: ContentStudentView[] = currentList.concat(newList);
    const resultList: ContentStudentView[] = (_.chain(mergedlist)
        .filter((item: ContentStudentView) => Boolean(item.content && item.content.id))
        .uniq(false, (item: ContentStudentView) => item.content.id)
        .value() as unknown) as ContentStudentView[];

    return {
        ...state,
        loading: false,
        hasError: false,
        listData: {
            ...state.listData,
            [listName]: {
                ...state.listData[listName],
                list: resultList,
                total: action.payload.total,
                loading: false,
            },
        },
    };
};

const removeContentFromList = (state: ItensState, action: AnyAction, listName: ViewType, contentId: number) => {
    const currentList: ContentStudentView[] = [...state.listData[listName].list];
    const resultList: ContentStudentView[] = currentList.filter((item: ContentStudentView) => item.content.id !== contentId);
    return {
        ...state,
        loading: false,
        hasError: false,
        listData: {
            ...state.listData,
            [listName]: {
                ...state.listData[listName],
                list: resultList,
                loading: false,
            },
        },
    };
};

const reducer: Reducer<ItensState> = (state: ItensState = INITIAL_STATE, action) => {
    switch (action.type) {
        case PURGE:
            return { ...INITIAL_STATE };
        case ItemTypes.GET_ITEM_REQUEST:
            if (action.payload.refreshOnlyPath) {
                return { ...state };
            }
            return { ...state, loading: true };
        case ItemTypes.GET_ITEM_SUCCESS:
            return {
                ...state,
                loading: false,
                data: { ...state.data, itens: action.payload },
            };
        case ItemTypes.GET_ITEM_PATH_SUCCESS:
            const currentData = { ...state.currentData };
            if (currentData && currentData.content) {
                const oldPath: string = currentData.content.path;
                currentData.content.path = action.payload;

                if (currentData.content.availableLanguages) {
                    const availableLanguage: AvailableLanguage = currentData.content.availableLanguages.find(
                        (available: AvailableLanguage) => available.path === oldPath,
                    ) as AvailableLanguage;

                    if (availableLanguage) {
                        availableLanguage.path = action.payload;
                    }
                }
            }
            return {
                ...state,
                loading: false,
                data: { ...state.data, itens: { ...state.data.itens, path: action.payload } },
                currentData,
            };
        case ItemTypes.GET_ITEM_FAILURE:
            return {
                ...state,
                loading: false,
                hasError: true,
                error: action.payload,
                data: {} as Itens,
            };
        case ItemTypes.GET_ITEMS_REQUEST:
            return { ...state, loading: true };
        case ItemTypes.GET_ITEMS_SUCCESS:
            return {
                ...state,
                loading: false,
                data: { ...state.data, itens: action.payload.data, total: action.payload.total },
            };
        case ItemTypes.GET_ITEMS_FAILURE:
            return {
                ...state,
                loading: false,
                hasError: true,
                error: action.payload,
                data: {} as Itens,
            };
        case ItemTypes.POST_ITEM_REQUEST:
            return { ...state, loading: true };
        case ItemTypes.POST_ITEM_SUCCESS:
            return {
                ...state,
                loading: false,
                success: true,
                data: {
                    ...state.data,
                    itens: { ...state.data.itens, ...action.payload },
                },
            };
        case ItemTypes.POST_ITEM_FAILURE:
            return {
                ...state,
                loading: false,
                hasError: true,
                error: action.payload,
            };
        case ItemTypes.PUT_ITEM_REQUEST:
            return { ...state, loading: true };
        case ItemTypes.PUT_ITEM_SUCCESS:
            return {
                ...state,
                loading: false,
                success: true,
            };
        case ItemTypes.PUT_ITEM_FAILURE:
            return {
                ...state,
                loading: false,
                hasError: true,
                error: action.payload,
            };
        case ItemTypes.DELETE_ITEM_REQUEST:
            return { ...state, loading: true, refreshItem: false };
        case ItemTypes.DELETE_ITEM_SUCCESS:
            return {
                ...state,
                loading: false,
                refreshItem: true,
            };
        case ItemTypes.DELETE_ITEM_FAILURE:
            return {
                ...state,
                loading: false,
                hasError: true,
            };
        case ItemTypes.PUBLISH_ITEM_REQUEST:
            return { ...state, loading: true };
        case ItemTypes.PUBLISH_ITEM_SUCCESS:
            return {
                ...state,
                loading: false,
            };
        case ItemTypes.PUBLISH_ITEM_FAILURE:
            return {
                ...state,
                loading: false,
                hasError: true,
            };
        case ItemTypes.ARCHIVE_ITEM_REQUEST:
            return { ...state, loading: true };
        case ItemTypes.ARCHIVE_ITEM_SUCCESS:
            return {
                ...state,
                loading: false,
            };
        case ItemTypes.ARCHIVE_ITEM_FAILURE:
            return {
                ...state,
                loading: false,
            };
        case ItemTypes.TOGGLE_ITEM_VISIBILITY_SUCCESS:
            return {
                ...state,
                loading: false,
                data: {
                    ...state.data,
                    itens: state.data.itens?.map((item) => {
                        if (item.id === action.payload) item.visibility = !item.visibility;
                        return item;
                    }),
                },
            };
        case ItemTypes.TOGGLE_ITEM_VISIBILITY_FAILURE:
            return {
                ...state,
                loading: false,
                hasError: true,
            };
        case ItemTypes.TOGGLE_ITEM_VISIBILITY_CLEAR:
            return {
                ...state,
                hasError: false,
            };
        case ItemTypes.ARCHIVE_ITEM_CLEAR:
        case ItemTypes.PUBLISH_ITEM_CLEAR:
        case ItemTypes.GET_ITEMS_CLEAR:
        case ItemTypes.POST_ITEM_CLEAR:
        case ItemTypes.PUT_ITEM_CLEAR:
        case ItemTypes.CONTENTS_CLEAR:
            return INITIAL_STATE;
        case ItemTypes.GET_CONTENT_LIST_REQUEST:
            return {
                ...state,
                loading: true,
                hasError: false,
            };
        case ItemTypes.LAST_PUBLISHED_SUCCESS:
            return processContentRequestSuccess(state, action, 'listLastPublishedStudentView');
        case ItemTypes.MOST_ACCESSED_SUCCESS:
            return processContentRequestSuccess(state, action, 'listMostAccessedStudentView');
        case ItemTypes.IN_PROGRESS_SUCCESS:
            return processContentRequestSuccess(state, action, 'listInProgressStudentView');
        case ItemTypes.COMPLETED_SUCCESS:
            return processContentRequestSuccess(state, action, 'listCompletedStudentView');
        case ItemTypes.TOP_RATED_SUCCESS:
            return processContentRequestSuccess(state, action, 'listTopRatedStudentView');
        case ItemTypes.FAVORITES_SUCCESS:
            return processContentRequestSuccess(state, action, 'listFavoriteStudentView');
        case ItemTypes.FOR_YOU_SUCCESS:
            return processContentRequestSuccess(state, action, 'listForYouStudentView');
        case ItemTypes.LIKE_YOU_SUCCESS:
            return processContentRequestSuccess(state, action, 'listLikeYouStudentView');
        case ItemTypes.SEARCHED_SUCCESS:
            return processContentRequestSuccess(state, action, 'listSearchResultStudentView');
        case ItemTypes.SUGGESTED_SUCCESS:
            return processContentRequestSuccess(state, action, 'listSuggestedStudentView');
        case ItemTypes.ESSENTIALS_SUCCESS:
            return processContentRequestSuccess(state, action, 'listEssentialStudentView');
        case ItemTypes.CATEGORIZED_SUCCESS:
            const listName: ViewType = (Constants.listCategorizedPrefix + action.payload.categoryId) as ViewType;
            return processContentRequestSuccess(state, action, listName);
        case ItemTypes.REMOVE_CONTENT_FROM_LIST:
            return removeContentFromList(state, action, 'listFavoriteStudentView', action.payload.contentId);
        case ItemTypes.ADD_LIST_BY_CATEGORY:
            return {
                ...state,
                listData: {
                    ...state.listData,
                    [Constants.listCategorizedPrefix + action.payload.categoryId]: { ...initialStateContentList },
                },
            };

        case ItemTypes.GET_CONTENT_LIST_FAILURE:
            return { ...state, loading: false, hasError: true };
        case ItemTypes.CLEAR_CONTENT_LIST:
            return { ...state, listData: { ...state.listData, [action.payload]: { ...initialStateContentList } }, loading: false, hasError: false };
        case ItemTypes.GET_CONTENT_REQUEST:
            return { ...state, loading: true, hasError: false };
        case ItemTypes.GET_CONTENT_SUCCESS:
            return { ...state, currentData: { ...action.payload }, loading: false, hasError: false };
        case ItemTypes.GET_CONTENT_FAILURE:
            return { ...state, loading: false, hasError: true };
        case ItemTypes.CLEAR_CONTENT:
            return { ...state, currentData: {} as ContentStudentView };
        case ItemTypes.START_CONTENT_SUCCESS:
            let incompleteContentEnrollment = { status: Constants.contentEnrollmentStatus.INCOMPLETE } as ContentEnrollment;
            const currentContentStudentView = _.uniq(
                _(state.listData)
                    .chain()
                    .toArray()
                    .pluck('list')
                    .flatten()
                    .compact()
                    .filter(
                        (contentStudentView: ContentStudentView) =>
                            contentStudentView.content.id === action.payload &&
                            (!contentStudentView.contentEnrollment ||
                                contentStudentView.contentEnrollment.status === Constants.contentEnrollmentStatus.NOT_ATTEMPTED),
                    )
                    .each((contentStudentView: ContentStudentView) => {
                        if (contentStudentView.contentEnrollment) {
                            incompleteContentEnrollment = {
                                ...contentStudentView.contentEnrollment,
                                status: Constants.contentEnrollmentStatus.INCOMPLETE,
                            };
                        }
                        contentStudentView.contentEnrollment = incompleteContentEnrollment;
                    })
                    .value(),
                false,
                (item: ContentStudentView) => item.content.id,
            );

            let { total } = state.listData.listInProgressStudentView;

            if (currentContentStudentView.length > 0) {
                total++;
            }

            return {
                ...state,
                listData: {
                    ...state.listData,
                    listInProgressStudentView: {
                        ...state.listData.listInProgressStudentView,
                        list: [...currentContentStudentView, ...state.listData.listInProgressStudentView.list],
                        total,
                    },
                },
            };
        case ItemTypes.FINISH_CONTENT_REQUEST:
            return { ...state, hasError: false };
        case ItemTypes.FINISH_CONTENT_SUCCESS:
            let completedContentEnrollment = { status: Constants.contentEnrollmentStatus.COMPLETED } as ContentEnrollment;
            _(state.listData)
                .chain()
                .toArray()
                .pluck('list')
                .flatten()
                .compact()
                .filter((contentStudentView: ContentStudentView) => contentStudentView.content.id === action.payload)
                .each((contentStudentView: ContentStudentView) => {
                    if (contentStudentView.contentEnrollment) {
                        completedContentEnrollment = { ...contentStudentView.contentEnrollment, status: Constants.contentEnrollmentStatus.COMPLETED };
                    }
                    contentStudentView.contentEnrollment = completedContentEnrollment;
                })
                .value();

            return {
                ...state,
                currentData: { ...state.currentData, contentEnrollment: completedContentEnrollment },
                loading: false,
            };

        case ItemTypes.FINISH_CONTENT_FAILURE:
            return { ...state, loading: false, hasError: true };
        case ItemTypes.CLEAR_CURRENT_CONTENT_STUDENT_VIEW_FROM_LIST_IN_PROGRESS:
            const listInProgress = _(state.listData.listInProgressStudentView.list).filter(
                (contentStudentView: ContentStudentView) => contentStudentView.content.id !== action.payload,
            );
            return {
                ...state,
                listData: {
                    ...state.listData,
                    listInProgressStudentView: {
                        ...state.listData.listInProgressStudentView,
                        list: listInProgress,
                        total: listInProgress.length,
                    },
                },
            };
        case ItemTypes.SET_CURRENT_CONTENT:
            return { ...state, currentData: { ...state.currentData, content: action.payload }, loading: false, hasError: false };
        case ItemTypes.SET_CONTENT_LINK_METADATA_SUCCESS:
            return {
                ...state,
                currentData: {
                    ...state.currentData,
                    content: {
                        ...state.currentData.content,
                        linkMetadata: action.payload,
                    },
                },
                loading: false,
                hasError: false,
            };
        case ItemTypes.CONTENTS_SEARCHED_CLEAR: {
            return {
                ...state,
                listData: {
                    ...state.listData,
                    listSearchResultStudentView: { ...initialStateContentList },
                },
            };
        }
        case ItemTypes.CONTENTS_COMPLETED_CLEAR: {
            return {
                ...state,
                listData: {
                    ...state.listData,
                    listCompletedStudentView: { ...initialStateContentList },
                },
            };
        }
        case ItemTypes.INCREASE_TOTAL_SUGGESTED:
            return {
                ...state,
                listData: {
                    ...state.listData,
                    listSearchResultStudentView: {
                        ...state.listData.listSearchResultStudentView,
                        total: ++state.listData.listSearchResultStudentView.total,
                    },
                },
            };

        case ItemTypes.DECREASE_TOTAL_SUGGESTED:
            return {
                ...state,
                listData: {
                    ...state.listData,
                    listSearchResultStudentView: {
                        ...state.listData.listSearchResultStudentView,
                        total: --state.listData.listSearchResultStudentView.total,
                    },
                },
            };
        case ItemTypes.UPDATE_CONTENT_STUDENT_VIEW_REQUEST:
            return { ...state, hasError: false };
        case ItemTypes.UPDATE_CONTENT_STUDENT_VIEW_SUCCESS:
            return { ...state, currentData: { ...action.payload }, loading: false, hasError: false };
        case ItemTypes.UPDATE_CONTENT_STUDENT_VIEW_FAILURE:
            return { ...state, loading: false, hasError: true };
        case ItemTypes.TRAININGS_SUCCESS:
            return processContentRequestSuccess(state, action, 'listTrainingsEnrolledStudentView');
        case ItemTypes.TRAININGS_CLEAR: {
            return {
                ...state,
                listData: {
                    ...state.listData,
                    listTrainingsEnrolledStudentView: { ...initialStateContentList },
                },
            };
        }
        default:
            return state;
    }
};

export default reducer;
